import { EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSiteByOrgAndSiteID } from 'reduxStore/sites/selectors';
import { sitesActions } from 'reduxStore/sites/slice';
import { SelectAddressAutoComplete, UploadBox } from 'zComponents/atoms';
import { Button, Checkbox, Flex, Form, Input, InputNumber, Modal, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const UpdateSiteModal = ({ children, open = false, setOpen, siteID }) => {
	// Hooks
	const [form] = Form.useForm();
	const {
		token: { fontWeightStrong, paddingXL, colorText, colorTextLabel, fontSizeSM, paddingSM, paddingXXL = 48 }
	} = theme.useToken();

	// Refs
	const uploadRef = useRef(null);
	const initialValuesRef = useRef(null);

	// Redux
	const dispatch = useDispatch();
	const siteInfo = useSelector((state) => selectSiteByOrgAndSiteID(state, siteID));
	const { loadingUpdate } = useSelector((state) => state.Sites);
	const photoUrl = siteInfo?.url || '';

	// Local State
	const [imagePath, setImagePath] = useState(null);
	const [image, setImage] = useState(null);
	const [imageChanged, setImageChanged] = useState(false);
	const [isChanged, setIsChanged] = useState(false);

	// Watch form values
	const watchedValues = Form.useWatch([], form);

	// Role-based permissions
	const isPermittedUpdateSite = useManagePermittedRoles('updateSite', {
		101: [siteInfo?.orgID],
		103: [siteInfo?.siteID]
	});

	// Populate form when modal opens
	useEffect(() => {
		if (open && siteInfo) {
			const initialValues = {
				name: siteInfo.name || '',
				address: siteInfo.fullAddress || '',
				xDimension: siteInfo.dimensionX || '',
				yDimension: siteInfo.dimensionY || '',
				addressLine1: siteInfo.addressLine1 || '',
				addressLine2: siteInfo.addressLine2 || '',
				suburb: siteInfo.suburb || '',
				state: siteInfo.state || '',
				country: siteInfo.country || '',
				postCode: siteInfo.postCode || '',
				latitude: siteInfo.latitude || '',
				longitude: siteInfo.longitude || '',
				url: siteInfo.url || ''
			};

			form.setFieldsValue(initialValues);
			initialValuesRef.current = initialValues;

			setImagePath(siteInfo.url || '');
			setImage(siteInfo.url || '');
			setImageChanged(false);
			setIsChanged(false);
		}
		if (!open) {
			form.resetFields();
			setIsChanged(false);
			setImageChanged(false);
		}
	}, [siteInfo, form, open]);

	// Track form changes to enable Save Changes button
	useEffect(() => {
		if (!watchedValues || !initialValuesRef.current) return;

		const {
			name,
			address,
			xDimension,
			yDimension,
			addressLine1,
			addressLine2,
			suburb,
			state,
			country,
			postCode,
			latitude,
			longitude
		} = watchedValues;

		const {
			name: initName,
			address: initAddress,
			xDimension: initX,
			yDimension: initY,
			addressLine1: initLine1,
			addressLine2: initLine2,
			suburb: initSuburb,
			state: initState,
			country: initCountry,
			postCode: initPost,
			latitude: initLat,
			longitude: initLong
		} = initialValuesRef.current;

		const formChanged =
			name !== initName ||
			address !== initAddress ||
			addressLine1 !== initLine1 ||
			addressLine2 !== initLine2 ||
			suburb !== initSuburb ||
			state !== initState ||
			country !== initCountry ||
			postCode !== initPost ||
			latitude !== initLat ||
			longitude !== initLong ||
			Number(xDimension) !== Number(initX) ||
			Number(yDimension) !== Number(initY);

		setIsChanged(formChanged || imageChanged);
	}, [watchedValues, imageChanged]);

	// Cancel
	const handleCancel = () => {
		form.resetFields();
		setOpen(false);
		setIsChanged(false);
		setImageChanged(false);
	};

	// Submit
	const handleFinish = async (values) => {
		await form.validateFields();

		const payload = {
			name: values.name,
			addressID: siteInfo.addressID || null,
			addressLine1: values.addressLine1 || null,
			addressLine2: values.addressLine2 || null,
			suburb: values.suburb || null,
			state: values.state || null,
			country: values.country || null,
			postCode: values.postCode || null,
			latitude: values.latitude || null,
			longitude: values.longitude || null,
			fullAddress: values.address || null,
			dimensionX: values.xDimension ? Number(values.xDimension) : null,
			dimensionY: values.yDimension ? Number(values.yDimension) : null,
			url: imagePath || null,
			mapID: siteInfo.mapID || null
		};

		dispatch(sitesActions.updateSite({ siteID, data: removeEmptyOrNull(payload) }));

		setTimeout(() => {
			handleCancel();
		}, 500);
	};

	return (
		<Fragment>
			<Tooltip title={isPermittedUpdateSite ? 'Update Site' : "You don't have permission to update this site"}>
				{children ? (
					children
				) : (
					<Button
						type='primary'
						icon={<EditOutlined />}
						onClick={() => setOpen(true)}
						disabled={!isPermittedUpdateSite}
					>
						Update Site
					</Button>
				)}
			</Tooltip>

			<Modal
				open={open}
				onCancel={() => {
					if (!loadingUpdate) {
						handleCancel();
					}
				}}
				closeIcon
				centered
				destroyOnClose
				width={683}
				footer={[
					<Flex gap={paddingSM} justify='flex-end'>
						<Button onClick={handleCancel} type='default' disabled={loadingUpdate}>
							Cancel
						</Button>
						<Button
							type='primary'
							htmlType='submit'
							form='siteForm'
							loading={loadingUpdate}
							disabled={!isChanged}
						>
							Update Site
						</Button>
					</Flex>
				]}
				styles={{ content: { padding: paddingXL }, footer: { marginTop: paddingXXL } }}
			>
				<Flex vertical style={{ marginBottom: paddingXL }}>
					<Title level={4} style={{ margin: 0, color: colorText, lineHeight: '28px' }}>
						Update Site
					</Title>
					<Text style={{ color: colorTextLabel, fontSize: fontSizeSM }}>Update your site details</Text>
				</Flex>

				<Form
					id='siteForm'
					key={siteID}
					form={form}
					layout='vertical'
					onFinish={handleFinish}
					requiredMark={false}
					name='siteForm'
				>
					<Flex vertical gap={paddingXL}>
						<Form.Item
							name='name'
							label={<Text style={{ fontWeight: fontWeightStrong }}>Site Name</Text>}
							style={{
								fontWeight: fontWeightStrong,
								marginBottom: 0
							}}
							rules={[{ required: true, message: 'Please enter a site name' }]}
						>
							<Input placeholder='eg. Prospect site 1' />
						</Form.Item>

						<Form.Item
							name='address'
							label={'Address'}
							style={{
								fontWeight: fontWeightStrong,
								marginBottom: 0
							}}
						>
							<SelectAddressAutoComplete
								value={form.getFieldValue('address')}
								onChange={(parsedAddress) => {
									form.setFieldsValue({
										addressLine1: parsedAddress.addressLine1,
										addressLine2: parsedAddress.addressLine2 || null,
										suburb: parsedAddress.suburb,
										state: parsedAddress.state,
										country: parsedAddress.country,
										postCode: parsedAddress.postCode,
										latitude: parsedAddress.latitude,
										longitude: parsedAddress.longitude,
										address: parsedAddress.formattedAddress
									});
								}}
							/>
						</Form.Item>

						{/* Hidden fields for Address */}
						<Form.Item name='addressLine1' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='addressLine2' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='suburb' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='state' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='country' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='postCode' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='latitude' hidden>
							<Input />
						</Form.Item>
						<Form.Item name='longitude' hidden>
							<Input />
						</Form.Item>

						<Flex gap={16}>
							<Form.Item
								name='xDimension'
								label={
									<Text style={{ color: colorTextLabel }} strong>
										X Dimension
									</Text>
								}
								style={{ width: '100%', marginBottom: 0 }}
								dependencies={['yDimension', 'url']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const y = getFieldValue('yDimension');
											if ((y || imagePath) && !value) {
												return Promise.reject(new Error('X Dimension is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<InputNumber placeholder='eg. 20' style={{ width: '100%' }} min={0} step={1} />
							</Form.Item>

							<Form.Item
								name='yDimension'
								label={
									<Text style={{ color: colorTextLabel }} strong>
										Y Dimension
									</Text>
								}
								style={{ width: '100%', marginBottom: 0 }}
								dependencies={['xDimension', 'url']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const x = getFieldValue('xDimension');
											if ((x || imagePath) && !value) {
												return Promise.reject(new Error('Y Dimension is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<InputNumber placeholder='eg. 24.5' style={{ width: '100%' }} min={0} step={1} />
							</Form.Item>
						</Flex>

						<Flex vertical gap={16}>
							<Form.Item
								label={<Text style={{ fontWeight: fontWeightStrong }}>Site Layout</Text>}
								name='url'
								style={{ marginBottom: 0 }}
								dependencies={['xDimension', 'yDimension']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const x = getFieldValue('xDimension');
											const y = getFieldValue('yDimension');
											if ((x || y) && !imagePath) {
												return Promise.reject(new Error('Site Layout is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<UploadBox
									entityID={3}
									photoUrl={photoUrl || null}
									entityName='Floor'
									shape='square'
									cursor
									edit
									size={261}
									uploadRef={uploadRef}
									setImage={setImage}
									image={image}
									setImagePath={(path) => {
										setImagePath(path);
										setImageChanged(path !== photoUrl);
									}}
									imagePath={imagePath}
									placeHolderUpload='Drag and drop or click to update the floor plan'
								/>
							</Form.Item>
						</Flex>

						{imageChanged && (
							<Flex align='start' gap={8}>
								<Form.Item
									name='confirmDisassociate'
									valuePropName='checked'
									style={{ margin: 0 }}
									rules={[
										{
											type: 'boolean',
											required: true,
											message: 'Please confirm'
										}
									]}
									validateTrigger={['onChange', 'onBlur']}
								>
									<Flex gap={8} align='start'>
										<Checkbox />
										<Text>
											I understand that uploading a new floor plan will disassociate all devices
											currently associated with this map.
										</Text>
									</Flex>
								</Form.Item>
							</Flex>
						)}
					</Flex>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default UpdateSiteModal;
